import React, { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Link } from "react-router-dom";
import { Target, Save } from "lucide-react";

const inputCls = "w-full bg-transparent border border-white/15 px-2.5 py-1.5 text-xs text-ink-50 focus:border-gold outline-none";
const PROPERTY_TYPES = ["Appartamento", "Villa", "Attico", "Loft", "Casa indipendente", "Cantina", "Uffici", "Locali commerciali", "Box auto"];

export default function MatchProfile({ lead, onSaved }) {
    const isSeller = lead.kind === "seller";
    return isSeller ? <SellerForm lead={lead} onSaved={onSaved} /> : <BuyerProfile lead={lead} onSaved={onSaved} />;
}

function BuyerProfile({ lead, onSaved }) {
    const sp = lead.search_profile || {};
    const [f, setF] = useState({
        transaction_type: sp.transaction_type || "", budget_min: sp.budget_min || "", budget_max: sp.budget_max || "",
        cities: (sp.cities || []).join(", "), property_types: sp.property_types || [], min_rooms: sp.min_rooms || "",
        min_surface: sp.min_surface || "", features: (sp.features || []).join(", "), urgency: sp.urgency || "", notes: sp.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const [matches, setMatches] = useState(null);

    const loadMatches = async () => {
        try { const r = await api.get(`/crm/leads/${lead.id}/matches`); setMatches(r.data); } catch (e) { console.error(e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (lead.search_profile) loadMatches(); }, [lead.id]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/crm/leads/${lead.id}/profile`, { search_profile: {
                ...f,
                cities: f.cities.split(",").map((x) => x.trim()).filter(Boolean),
                features: f.features.split(",").map((x) => x.trim()).filter(Boolean),
            } });
            onSaved?.();
            await loadMatches();
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="buyer-profile" className="border border-white/10 bg-ink-900/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400"><Target size={13} className="text-gold" /> Ricerca del cliente</div>
            <div className="grid grid-cols-2 gap-2.5">
                <div>
                    <label className="block text-[9px] text-ink-500 uppercase mb-1">Tipo</label>
                    <select data-testid="sp-transaction" value={f.transaction_type} onChange={(e) => setF({ ...f, transaction_type: e.target.value })} className="w-full bg-ink-950 border border-white/15 px-2 py-1.5 text-xs text-ink-50 outline-none">
                        <option value="">—</option><option value="vendita">Acquisto</option><option value="affitto">Affitto</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[9px] text-ink-500 uppercase mb-1">Urgenza</label>
                    <select value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} className="w-full bg-ink-950 border border-white/15 px-2 py-1.5 text-xs text-ink-50 outline-none">
                        <option value="">—</option><option value="alta">Alta</option><option value="media">Media</option><option value="bassa">Bassa</option>
                    </select>
                </div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Budget min €</label>
                    <input data-testid="sp-budget-min" type="number" value={f.budget_min} onChange={(e) => setF({ ...f, budget_min: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Budget max €</label>
                    <input data-testid="sp-budget-max" type="number" value={f.budget_max} onChange={(e) => setF({ ...f, budget_max: e.target.value })} className={inputCls} /></div>
                <div className="col-span-2"><label className="block text-[9px] text-ink-500 uppercase mb-1">Zone / città (separate da virgola)</label>
                    <input data-testid="sp-cities" value={f.cities} onChange={(e) => setF({ ...f, cities: e.target.value })} placeholder="Milano, Monza..." className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Vani min</label>
                    <input data-testid="sp-rooms" type="number" value={f.min_rooms} onChange={(e) => setF({ ...f, min_rooms: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Mq min</label>
                    <input type="number" value={f.min_surface} onChange={(e) => setF({ ...f, min_surface: e.target.value })} className={inputCls} /></div>
                <div className="col-span-2">
                    <label className="block text-[9px] text-ink-500 uppercase mb-1">Tipologie</label>
                    <div className="flex flex-wrap gap-1.5">
                        {PROPERTY_TYPES.map((t) => (
                            <button key={t} type="button" onClick={() => setF({ ...f, property_types: f.property_types.includes(t) ? f.property_types.filter((x) => x !== t) : [...f.property_types, t] })}
                                className={`text-[10px] px-2 py-1 border transition-colors ${f.property_types.includes(t) ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:text-ink-100"}`}>{t}</button>
                        ))}
                    </div>
                </div>
                <div className="col-span-2"><label className="block text-[9px] text-ink-500 uppercase mb-1">Caratteristiche richieste (virgola)</label>
                    <input value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} placeholder="giardino, box, ascensore..." className={inputCls} /></div>
            </div>
            <button data-testid="sp-save" onClick={save} disabled={busy} className="cta-gold px-4 py-2 text-xs flex items-center gap-2"><Save size={12} /> {busy ? "Salvataggio..." : "Salva ricerca"}</button>

            {matches && (
                <div data-testid="lead-matches" className="pt-2 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-2">Immobili compatibili ({(matches.items || []).length})</div>
                    {(matches.items || []).length === 0 && <div className="text-[11px] text-ink-500">Nessun immobile in portafoglio compatibile con questa ricerca.</div>}
                    {(matches.items || []).map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2 py-1.5 text-xs border-b border-white/5">
                            <Link to={`/annunci/${p.id}`} className="text-ink-100 truncate hover:text-gold">{p.title}</Link>
                            <span className="text-ink-400 text-[10px] shrink-0">{formatPrice(p.price)}</span>
                            <span data-testid={`match-score-${p.id}`} className={`shrink-0 text-[10px] px-1.5 py-0.5 border ${p.score >= 80 ? "border-green-700/40 text-green-300" : p.score >= 60 ? "border-gold/40 text-gold" : "border-white/15 text-ink-400"}`}>{p.score}%</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SellerForm({ lead, onSaved }) {
    const si = lead.seller_info || {};
    const [f, setF] = useState({
        address: si.address || "", city: si.city || "", property_type: si.property_type || "",
        surface: si.surface || "", expected_price: si.expected_price || "", mandate_end: si.mandate_end || "",
        exclusive: !!si.exclusive, notes: si.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await api.put(`/crm/leads/${lead.id}/profile`, { seller_info: f }); onSaved?.(); }
        finally { setBusy(false); }
    };
    return (
        <div data-testid="seller-info" className="border border-white/10 bg-ink-900/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400"><Target size={13} className="text-gold" /> Immobile del venditore (acquisizione)</div>
            <div className="grid grid-cols-2 gap-2.5">
                <div className="col-span-2"><label className="block text-[9px] text-ink-500 uppercase mb-1">Indirizzo</label>
                    <input data-testid="si-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Città</label>
                    <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Tipologia</label>
                    <input value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Mq</label>
                    <input type="number" value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Prezzo atteso €</label>
                    <input data-testid="si-price" type="number" value={f.expected_price} onChange={(e) => setF({ ...f, expected_price: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[9px] text-ink-500 uppercase mb-1">Scadenza incarico</label>
                    <input data-testid="si-mandate" type="date" value={f.mandate_end} onChange={(e) => setF({ ...f, mandate_end: e.target.value })} className={inputCls} /></div>
                <div className="flex items-end gap-2 pb-1">
                    <input id="si-excl" type="checkbox" checked={f.exclusive} onChange={(e) => setF({ ...f, exclusive: e.target.checked })} className="accent-[#D4AF6A]" />
                    <label htmlFor="si-excl" className="text-xs text-ink-100">Esclusiva</label>
                </div>
                <div className="col-span-2"><label className="block text-[9px] text-ink-500 uppercase mb-1">Note</label>
                    <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className={inputCls} /></div>
            </div>
            <button data-testid="si-save" onClick={save} disabled={busy} className="cta-gold px-4 py-2 text-xs flex items-center gap-2"><Save size={12} /> {busy ? "Salvataggio..." : "Salva dati"}</button>
        </div>
    );
}
